<template>
  <ai-carousel-overflow
    v-model="currentStep"
    access-id="HotelOffersAndPackagesCarousel"
    class="HotelOffersAndPackagesCarousel-carousel"
    :items="items"
    :slide-width="slideWidth"
    :title="t('hotel.offersAndPackages.title')"
    :subtitle="t('hotel.offersAndPackages.subtitle')"
    :description="description ?? ''"
    :aria-label="$t('accessibility.description.hotelOffersAndPackagesCarousel')"
    show-cta
    :link="allOffersLink"
    :cta-label="t('hotel.offersAndPackages.cta')"
    :hide-progress-bar="isSmallDevice"
    :component="HotelOffersAndPackagesCarouselItem"
    :carousel-only="false">
    <template #footer>
      <div class="HotelOffersAndPackagesCarousel-button">
        <ai-carousel-button
          @left-click="onPreviousSlide"
          @right-click="onNextSlide" />
      </div>
    </template>
  </ai-carousel-overflow>
</template>

<script setup lang="ts">
import HotelOffersAndPackagesCarouselItem from './HotelOffersAndPackagesCarouselItem.vue';

import type { CarouselOverflowItemProps } from '~~/domains/ux/organisms/carousels/types';
import type { OfferAndPackage as OfferAndPackageType } from '~~/domains/graphql';

interface Props {
  offersAndPackages: OfferAndPackageType[];
  description: string;
  allOffersLink?: string | null;
}

const props = defineProps<Props>();

const { t } = useI18n();

const items = computed<CarouselOverflowItemProps[]>(() =>
  (props.offersAndPackages ?? []).map(
    offerAndPackage =>
      ({
        cardAsLink: true,
        ctaLabel: t('hotel.offersAndPackages.item.cta'),
        description: shortenDescription(offerAndPackage.description ?? ''),
        detailLink: offerAndPackage.detailLink,
        key: offerAndPackage.id ?? '',
        picture: offerAndPackage.picture ?? null,
        title: offerAndPackage.sectionTitle ?? '',
        subtitle: offerAndPackage.title ?? '',
      }) as CarouselOverflowItemProps,
  ),
);

const isSmallDevice = useMatchMediaQuery('tablet');
const slideWidth = computed(() => (isSmallDevice.value ? 0.75 : 0.5));

const currentStep = ref<number>(1);

const onPreviousSlide = () => {
  if (currentStep.value === 1) {
    currentStep.value = items.value.length;
  } else {
    currentStep.value--;
  }
};

const onNextSlide = () => {
  if (currentStep.value === items.value.length) {
    currentStep.value = 1;
  } else {
    currentStep.value++;
  }
};

const shortenDescription = (itemDescription: string) => {
  if (itemDescription.length > 200)
    return itemDescription.slice(0, 197).concat('...');

  return itemDescription;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.HotelOffersAndPackages {
  background: constants.$linear-gradient-stratos;
  padding-top: constants.$padding-07;
  padding-bottom: constants.$padding-07;
  margin-top: constants.$margin-04;
}

.HotelOffersAndPackages-subtitle {
  color: colors.$gold-300;
  text-transform: uppercase;
  margin-bottom: constants.$margin-02;
}

.HotelOffersAndPackages-title {
  color: colors.$white;
  margin-bottom: constants.$margin-02;
}

.HotelOffersAndPackages-description {
  color: colors.$white;
  margin-bottom: constants.$margin-02;
}

.HotelOffersAndPackagesCarousel-carousel {
  position: relative;
}
.HotelOffersAndPackagesCarousel-button {
  position: absolute;

  top: calc(50% - (constants.$margin-04 + constants.$margin-02));
  right: calc(20% - constants.$margin-02);

  @media (mq.$from-medium) {
    display: none;
  }
}
</style>
